import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { getOrderData, getOrderCancel } from '../../redux/OrderSlice/OrderSelectors';
import { useAppDispatch } from '../../redux/Store';
import { cancelOrder } from '../../redux/OrderSlice/OrderThunks';
import { MerchantOrderStatus } from '../../interfaces/MerchantOrder';
import { RemoteStatus } from '../../redux/SharedTypes';
import SettingsItem from '../SettingsItem/SettingsItem';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import SvgIcon from '../SvgIcon/SvgIcon';
import moreDots from '../../images/moreDots.svg';
import infoCircle from '../../images/info_circle.svg';
import helpCircle from '../../images/help_circle.svg';
import externalLink from '../../images/external_link.svg';
import x_circle from '../../images/x_circle.svg';
import styles from './Settings.module.scss';
import TestIds from '../../tests/TestIds';

const messages = defineMessages({
	settings: {
		id: 'settings.settings',
		defaultMessage: 'Settings',
	},
	helpCenter: {
		id: 'settings.helpCenter',
		defaultMessage: 'Help Center',
	},
	termsAndConditions: {
		id: 'settings.termsAndConditions',
		defaultMessage: 'Terms and Conditions',
	},
	cancelOrder: {
		id: 'settings.cancelOrder',
		defaultMessage: 'Cancel Order',
	},
});

const ExternalLinks: FC = () => {
	const { formatMessage } = useIntl();

	return (
		<div className={styles.externalLinks}>
			<SettingsItem
				icon={helpCircle}
				label={formatMessage(messages.helpCenter)}
				actionIcon={externalLink}
				externalUrl="https://help.spectrocoin.com/"
			/>
			<SettingsItem
				icon={infoCircle}
				label={formatMessage(messages.termsAndConditions)}
				actionIcon={externalLink}
				externalUrl="https://spectrocoin.com/en/terms.html"
			/>
		</div>
	);
};

const Settings: FC = () => {
	const dispatch = useAppDispatch();
	const { id, status } = useSelector(getOrderData) || ({} as any);
	const { status: cancelStatus } = useSelector(getOrderCancel) || ({} as any);
	const [isOpen, setIsOpen] = useState(false);

	const closeSettings = () => {
		setIsOpen(false);
	};

	return (
		<div className={styles.container} data-cy={TestIds.Settings}>
			<div className={styles.dotsIconWrapper} onClick={() => setIsOpen(true)}>
				<SvgIcon className={styles.icon} src={moreDots} />
			</div>
			{isOpen && (
				<>
					<div className={styles.overlay} onClick={closeSettings} />
					<div className={styles.settings} data-cy={TestIds.SettingsOptions}>
						<div className={styles.heading}>
							<FormattedMessage {...messages.settings} />
							<FontAwesomeIcon
								className={styles.closeIcon}
								icon={faXmark}
								onClick={closeSettings}
							/>
						</div>
						<LanguageSwitcher closeSettings={closeSettings} />
						<ExternalLinks />
						{status === MerchantOrderStatus.NEW && (
							<div
								data-cy={TestIds.OrderCancel}
								className={styles.cancelOrder}
								onClick={() => {
									if (cancelStatus !== RemoteStatus.InProgress) {
										dispatch(cancelOrder(id));
										closeSettings();
									}
								}}
							>
								<SvgIcon className={styles.settingsIcon} src={x_circle} />
								<FormattedMessage {...messages.cancelOrder} />
							</div>
						)}
					</div>
				</>
			)}
		</div>
	);
};

export default Settings;
